import React from "react";
import { Sparkles, MessagesSquare, MapPin, Phone, Clock, Globe } from "lucide-react";

/**
 * Claude v2.4-inspired sidebar — pure data-driven from the public chatbot meta.
 *
 * - "What we do" / Features uses generic SaaS-friendly defaults.
 * - "Common questions" prefers the tenant-aware suggested_prompts from the
 *   backend (`/api/public/chatbot/{slug}` → `suggested_prompts`). These are
 *   the same industry- + KB-aware customer-facing prompts as the hero. If
 *   the backend hasn't computed any, fall back to the small generic set.
 * - Contact card shows real tenant info from `/api/public/chatbot/{slug}`.
 */
const DEFAULT_FAQS = [
  "What do you offer?",
  "Where are you located?",
  "How can I reach you?",
  "What are your business hours?",
  "How much does it cost?",
];

export default function ChatSidebar({ open, company, suggestedPrompts, onAsk, onOpenLead, onClose }) {
  const features = [
    {
      Icon: Sparkles,
      h: "Ask anything",
      sub: "Trained on this business — answers come from real content.",
    },
    {
      Icon: MessagesSquare,
      h: "Talk to a human",
      sub: "Leave your details — the team replies in their inbox.",
    },
  ];

  // v1.0.15 — prefer backend-provided tenant-aware prompts so the sidebar
  // matches the hero pills and reflects the tenant's industry/KB instead of
  // showing generic copy. Trim/dedupe and cap at 6 for visual breathing room.
  const dedup = new Set();
  const promptSource = Array.isArray(suggestedPrompts) && suggestedPrompts.length
    ? suggestedPrompts
    : DEFAULT_FAQS;
  const promptsToShow = [];
  for (const q of promptSource) {
    if (typeof q !== "string") continue;
    const t = q.trim();
    if (!t || dedup.has(t)) continue;
    dedup.add(t);
    promptsToShow.push(t);
    if (promptsToShow.length >= 6) break;
  }

  const c = company || {};
  return (
    <>
      <aside className={`rch-sidebar ${open ? "open" : ""}`} data-testid="rch-sidebar">
        <div className="rch-sidebar-inner">
          <div className="rch-section">
            <h4>What you can do</h4>
            {features.map(({ Icon, h, sub }) => (
              <div className="rch-feat" key={h}>
                <div className="rch-feat-ico"><Icon className="w-4 h-4" /></div>
                <div className="rch-feat-text">
                  <div className="rch-feat-h">{h}</div>
                  <div className="rch-feat-sub">{sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="rch-section">
            <h4>Common questions</h4>
            {promptsToShow.map((q) => (
              <div
                key={q}
                className="rch-faq-item"
                role="button"
                tabIndex={0}
                onClick={() => { onAsk?.(q); onClose?.(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { onAsk?.(q); onClose?.(); } }}
                data-testid={`rch-faq-${q.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span>{q}</span><span>→</span>
              </div>
            ))}
          </div>

          {(c.address || c.phone || c.business_hours || c.website) && (
            <div className="rch-section">
              <div className="rch-cta-card">
                <h5>Need help deciding?</h5>
                <p>Reach the team directly — happy to answer questions.</p>
                <div className="rch-cta-contact">
                  {c.phone && (
                    <div><Phone className="inline w-3 h-3 mr-1.5" /><a href={`tel:${c.phone}`}>{c.phone}</a></div>
                  )}
                  {c.address && (
                    <div><MapPin className="inline w-3 h-3 mr-1.5" />{c.address}</div>
                  )}
                  {c.business_hours && (
                    <div><Clock className="inline w-3 h-3 mr-1.5" />{c.business_hours}</div>
                  )}
                  {c.website && (
                    <div><Globe className="inline w-3 h-3 mr-1.5" /><a href={c.website} target="_blank" rel="noreferrer">{c.website.replace(/^https?:\/\//, "")}</a></div>
                  )}
                </div>
                <button
                  className="rch-cta-btn"
                  onClick={() => { onOpenLead?.(); onClose?.(); }}
                  data-testid="rch-sidebar-contact"
                >
                  Contact me
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
      <div
        className={`rch-backdrop ${open ? "show" : ""}`}
        onClick={onClose}
        data-testid="rch-backdrop"
      />
    </>
  );
}
